/**
 * Graha Positions — compact sidereal positions of all nine grahas for the day,
 * in the editorial grammar (docs/DAILY_PANCHANGA_UI_SPEC.md §8.5). Bridges the
 * Daily Panchanga to the Kāla Chakra view.
 */
import React, { useEffect, useState } from 'react';
import { API_CONFIG } from '../../config/api';
import type { LocationData } from '../LocationSearch/LocationSearch';
import type { PlanetaryPositionsResponse, PlanetPosition } from '../../types/api';
import { grahaWarmColor } from '../../lib/grahaVisual';

interface GrahaPositionsProps {
  date: Date;
  location: LocationData | null;
  onOpenKalaChakra?: () => void;
}

// Canonical Sanskrit name, in traditional graha order. The identity dot color
// comes from the shared warm palette (lib/grahaVisual.ts), matching the
// Planetary Positions cards so a Sūrya dot is the same gold on both surfaces.
const GRAHA_ORDER: { key: string; name: string }[] = [
  { key: 'sun', name: 'Sūrya' },
  { key: 'moon', name: 'Candra' },
  { key: 'mars', name: 'Kuja' },
  { key: 'mercury', name: 'Budha' },
  { key: 'jupiter', name: 'Guru' },
  { key: 'venus', name: 'Śukra' },
  { key: 'saturn', name: 'Śani' },
  { key: 'rahu', name: 'Rāhu' },
  { key: 'ketu', name: 'Ketu' },
];

const formatLocalDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default function GrahaPositions({ date, location, onOpenKalaChakra }: GrahaPositionsProps) {
  const [planets, setPlanets] = useState<PlanetPosition[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const base = API_CONFIG.baseUrl || window.location.origin;
    const params = new URLSearchParams({ date: formatLocalDate(date) });
    if (location) {
      params.set('latitude', String(location.latitude));
      params.set('longitude', String(location.longitude));
    }
    fetch(`${base}/api/v1/planetary/positions?${params.toString()}`)
      .then(r => (r.ok ? r.json() : null))
      .then((res: PlanetaryPositionsResponse | null) => {
        if (!cancelled && res?.planets) setPlanets(res.planets);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [date, location]);

  // Order the API planets by the canonical graha sequence.
  const byName = (p: PlanetPosition) => (p.planet_english || p.planet || '').toLowerCase();
  const ordered = planets
    ? GRAHA_ORDER.map(g => ({
        ...g,
        pos: planets.find(p => byName(p).includes(g.key)),
      })).filter(row => row.pos)
    : [];

  return (
    <section className="kg-warm rounded-2xl p-5" style={{ background: 'var(--kg-card)', border: '1px solid var(--kg-line)', boxShadow: 'var(--kg-shadow)' }}>
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h3 className="font-fraunces text-[18px] font-medium" style={{ color: 'var(--kg-ink)' }}>
          Graha Sthāna
          <span className="font-normal" style={{ color: 'var(--kg-sub)' }}> · Planetary Positions</span>
        </h3>
        {onOpenKalaChakra && (
          <button onClick={onOpenKalaChakra} className="kg-link text-[12.5px] font-semibold">
            Kāla Chakra &rarr;
          </button>
        )}
      </div>
      {!planets ? (
        <p className="text-[13px]" style={{ color: 'var(--kg-sub)' }}>Loading positions…</p>
      ) : (
        <div className="divide-y" style={{ borderColor: 'var(--kg-line)' }}>
          {ordered.map(row => {
            const p = row.pos!;
            const deg = Math.floor(p.degrees_in_sign);
            // Use the backend's motion label, not the raw speed sign — Rāhu/Ketu
            // are always retrograde by convention despite a briefly positive
            // true-node speed (see planetary_positions_manager.py).
            const retro = p.motion === 'RETROGRADE';
            return (
              <div key={row.key} className="flex items-center gap-3 py-2">
                <span
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ background: grahaWarmColor(row.key) }}
                  aria-hidden="true"
                />
                <span className="flex-1 text-[14px] font-medium" style={{ color: 'var(--kg-ink)' }}>
                  {row.name}{retro && <span title="retrograde" style={{ color: 'var(--kg-sub)' }}> ℞</span>}
                </span>
                <span className="text-[13px] tabular-nums" style={{ color: 'var(--kg-sub)' }}>
                  {p.zodiac_sign_sanskrit || p.zodiac_sign} {deg}°
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
